import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { InWorkOrFinishDeal, LostDeal, PostponeDeal, ReturnDealsToPipeline } from '@crm/features';
import { cn } from '@crm/shared/lib';
import { Button, SvgIcon } from '@crm/shared/ui';

import { actionsMap } from '../../model/actionsMap';
import { DealDetailsBlock } from '../DealDetailsBlock';

import type { TDealDetails } from '@crm/entities/deal';

interface QuickActionsBlockProps {
    dealDetailsData: TDealDetails;
}

const getInWorkOrFinishedInvalidationScope = (status: TDealDetails['dealData']['status']) => {
    if (status === 'lost') {
        return 'lost';
    }

    if (status === 'postponed') {
        return 'postponed';
    }

    return 'board';
};

export const QuickActionsBlock = ({ dealDetailsData }: QuickActionsBlockProps) => {
    const { t } = useTranslation();
    const { actionsData } = dealDetailsData;

    if (!actionsData) {
        return null;
    }

    return (
        <DealDetailsBlock title={t('crm.dealDetailsContent.quickActionsBlock.title')}>
            <div className="grid w-full grid-cols-2 gap-2">
                {actionsData?.actions.map((action, actionIndex) => {
                    const actionConfig = actionsMap[action.type];
                    const actionButton = (
                        <Button
                            type="button"
                            className={cn(
                                'h-10 w-full justify-center px-3 py-0 text-xs leading-4 font-normal hover:opacity-70',
                                actionConfig.stile
                            )}
                        >
                            <SvgIcon icon={actionConfig.icon} size="16" color="currentColor" />
                            {t(actionConfig.titleKey)}
                        </Button>
                    );

                    let actionElement: ReactNode;

                    if (action.type === 'to_lost') {
                        actionElement = (
                            <LostDeal
                                dealIds={[dealDetailsData.dealData.id]}
                                invalidationScope={
                                    dealDetailsData.dealData.status === 'postponed'
                                        ? 'postponed'
                                        : 'board'
                                }
                                trigger={actionButton}
                            />
                        );
                    } else if (action.type === 'to_postpone') {
                        actionElement = (
                            <PostponeDeal
                                dealIds={[dealDetailsData.dealData.id]}
                                trigger={actionButton}
                            />
                        );
                    } else if (action.type === 'to_board') {
                        actionElement = (
                            <ReturnDealsToPipeline
                                dealIds={[dealDetailsData.dealData.id]}
                                columns={dealDetailsData.pipelineColumnsData.columns}
                                trigger={actionButton}
                            />
                        );
                    } else if (action.type === 'to_complete') {
                        actionElement = (
                            <InWorkOrFinishDeal
                                dealIds={[dealDetailsData.dealData.id]}
                                invalidationScope={getInWorkOrFinishedInvalidationScope(
                                    dealDetailsData.dealData.status
                                )}
                                trigger={actionButton}
                            />
                        );
                    } else {
                        actionElement = actionButton;
                    }

                    return (
                        <div
                            key={action.type}
                            className={cn('min-w-0', actionIndex === 0 && 'col-span-2')}
                        >
                            {actionElement}
                        </div>
                    );
                })}
            </div>
        </DealDetailsBlock>
    );
};
